import React, { useEffect, useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const GameUserList = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    maxPlayTime: 0,
    maxLevelReached: 0,
    maxLevelCompleted: 0,
    maxBallSpeed: 0,
    totalCoins: 0
  });

  useEffect(() => {
    setLoading(true);
    const fetchMatches = async () => {
      try {
        const response = await api.get("/match/3d");
        const statsRes = await api.get("/match/stats/3d");

        setMatches(Array.isArray(response.data) ? response.data : []);
        setStats(statsRes.data);
      } catch {
        toast.error(t("gameUserList3d_fetchError"));
      } finally {
        setLoading(false);
      }
    };
    fetchMatches();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <Loader2 size={28} className="animate-spin text-violet-400" />
        <span className="text-sm text-zinc-500">{t("gameUserList3d_loading")}</span>
      </div>
    );
  }

  if (!loading && matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2 text-center border border-white/8 rounded-xl">
        <span className="text-zinc-500 text-sm">{t("gameUserList3d_noMatches")}</span>
        <span className="text-zinc-600 text-xs">{t("gameUserList3d_noMatchesDescription")}</span>
      </div>
    );
  }

  let index = 0;

  return (
    <>
    <div className="rounded-xl border border-white/8 bg-white/5 p-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">

        <div>
          <div className="text-zinc-400 text-xs">{t("gameUserList3d_statsPlaytime")}</div>
          <div className="text-green-400 text-lg font-bold">{stats.maxPlayTime ?? 0}</div>
        </div>

        <div>
          <div className="text-zinc-400 text-xs">{t("gameUserList3d_statsCoins")}</div>
          <div className="text-red-400 text-lg font-bold">{stats.totalCoins ?? 0}</div>
        </div>

        <div>
          <div className="text-zinc-400 text-xs">{t("gameUserList3d_statsLevelReached")}</div>
          <div className="text-white text-lg font-bold">
            {stats.maxLevelReached ?? 0}
          </div>
        </div>

        <div>
          <div className="text-zinc-400 text-xs">{t("gameUserList3d_statsMaxBallSpeed")}</div>
          <div className="text-blue-400 text-lg font-bold">
            {stats.maxBallSpeed.toFixed(1) ?? 0}
          </div>
        </div>
      </div>
    </div>
    <div className="rounded-xl border border-white/8 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/8 hover:bg-transparent">
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList_date")}</TableHead>
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList3d_tablePlayTime")}</TableHead>
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList3d_tableCoins")}</TableHead>
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList3d_tableLevelReached")}</TableHead>
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList3d_tableLevelsCompleted")}</TableHead>
            <TableHead className="text-zinc-500 text-xs uppercase tracking-wider font-medium text-center">{t("gameUserList3d_tableMaxBallSpeed")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {matches.map((match) => {
            return (
              <TableRow key={match.id} className="border-white/5 hover:bg-white/3 transition-colors">
                <TableCell className="text-zinc-300 text-sm text-center">
                  {`${new Date(match.playedAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} - ${new Date(match.playedAt).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\.\s?/g, '/')}`}
                </TableCell>
                <TableCell className="text-zinc-300 text-sm text-center">{match.totalPlaySeconds}</TableCell>
                <TableCell className="text-zinc-300 text-sm text-center">{match.coins}</TableCell>
                <TableCell className="text-zinc-300 text-sm text-center">{match.levelReached}</TableCell>
                <TableCell className="text-zinc-300 text-sm text-center">{match.levelsCompleted}</TableCell>
                <TableCell className="text-zinc-300 text-sm text-center">{match.maxBallSpeed.toFixed(1)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
    </>
  );
};

export default GameUserList;
